import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  SimpleChanges,
  OnInit,
  OnChanges,
  forwardRef
} from '@angular/core';
import { Logger } from '@app/logger/logger';
import { MatLegacyOption } from '@angular/material/legacy-core';
import { getIdentifierForIterableItem } from '@app/shared/utilities/trackby.utility';

/**
 * This component provides a custom implementation of a multi-select mat-select.
 * It provides Select All/Deselect All capabilities and can be used with any type
 * as long as the type implements ICheckboxMultiSelectOption.
 *
 * The component also implements ControlValueAccessor and so can also be used with
 * angular template or reactive forms. This is not required. The parent component can
 * instead use the Output `selectedOptionsChange` to receive data.
 *
 */
@Component({
  selector: 'acs-checkbox-multi-select',
  templateUrl: './checkbox-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckBoxMultiSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CheckBoxMultiSelectComponent),
      multi: true
    }
  ]
})
export class CheckBoxMultiSelectComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator {
  @ViewChild('allSelectionElement') private allSelectionElement: MatLegacyOption;

  @Input() inputOptions: Array<ICheckboxMultiSelectOption>;
  @Input() fieldPlaceholder = 'Select Option';
  @Input() selectedOptions: Array<any>;
  @Input() isRequired = false;
  @Input() selectAllLabel = 'Select/Deselect All';

  @Output() selectedOptionsChange: EventEmitter<any[]> = new EventEmitter();

  public selectOptionsForm: FormGroup;
  public getIdentifierForIterableItem = getIdentifierForIterableItem;

  private get modelOptFormControl(): AbstractControl {
    return this.selectOptionsForm.controls.modelOptions;
  }

  constructor(private formBuilder: FormBuilder, private logger: Logger) {
    this.selectOptionsForm = this.formBuilder.group({
      modelOptions: new FormControl({ value: '', disabled: true })
    });
  }

  ngOnInit(): void {
    this.inputOptions = this.inputOptions.filter((data) => data.msoKey !== 'NULL');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.selectedOptions?.currentValue) {
      this.selectedOptionsChange.emit([]);
      this.modelOptFormControl.patchValue([]);
    } else if (
      changes.selectedOptions &&
      changes.selectedOptions.currentValue !== changes.selectedOptions.previousValue
    ) {
      const matchedOptions = this.inputOptions.filter((option) =>
        this.selectedOptions?.includes(option.msoKey)
      );

      const visibleInputOptions = this.inputOptions.filter((x) => x.msoIsVisible);
      if (matchedOptions.length === visibleInputOptions.length) {
        this.modelOptFormControl.patchValue([...visibleInputOptions, 0]);
      } else {
        this.modelOptFormControl.patchValue(matchedOptions);
      }
    }

    this.inputOptions.length
      ? this.modelOptFormControl.enable()
      : this.modelOptFormControl.disable();
  }

  public toggleAllSelection() {
    if (this.allSelectionElement.selected) {
      this.modelOptFormControl.patchValue([
        ...this.inputOptions.filter((item) => item.msoIsVisible),
        0
      ]);

      this.sendData(this.modelOptFormControl.value);
    } else {
      this.modelOptFormControl.patchValue([]);
      this.sendData(this.modelOptFormControl.value);
    }
  }

  public toggleOption(): void {
    if (this.allSelectionElement.selected) {
      this.allSelectionElement.deselect();
    }

    if (this.selectOptionsForm.controls.modelOptions.value.length === this.inputOptions.length) {
      this.allSelectionElement.select();
    }

    if (this.selectOptionsForm.controls.modelOptions.value.length > this.inputOptions.length) {
      this.sendData(this.selectOptionsForm.controls.modelOptions.value.slice(1));
    } else {
      this.sendData(this.selectOptionsForm.controls.modelOptions.value);
    }
  }

  /**
   * Can be used to set the initial value on the form
   */
  public writeValue(obj: any): void {
    if (obj) {
      this.selectedOptions = obj;
    }
  }

  /**
   * Registers a callback that can be fired when the form control data is changed.
   * It is our responsibility to call `propagateChange` when necessary.
   */
  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback that can be fired when the form control is touched/blurred.
   * It is our responsibility to call `onTouched` when necessary.
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * A hook into the validation logic of the form control.
   * Implement custom validation logic here.
   */
  public validate(control: AbstractControl<any, any>): ValidationErrors {
    this.logger.log('CheckBoxMultiSelectComponent.validate', control);
    if (this.isRequired && !Boolean(control.value?.length)) {
      return { required: true };
    }

    return null;
  }

  private propagateChange = (_: any) => {};
  private onTouched = () => {};

  private sendData(data): void {
    this.logger.log('CheckBoxMultiSelectComponent.sendData', data);
    this.onTouched();
    this.propagateChange(data.map((x) => x.msoKey));
    this.selectedOptionsChange.emit(data);
  }
}

// should be implemented as getters
export interface ICheckboxMultiSelectOption {
  readonly msoKey: number | string;
  readonly msoDescriptor: string;
  readonly msoIsVisible: boolean;
}
